import TInterviewThread, {
    isInterviewMessageContentFormat,
    isInterviewGoalAttachmentType,
    isInterviewMessageRole,
    isInterviewThreadStatus,
    TInterviewGoalStatus,
    TInterviewMessage,
    TInterviewGoalAttachment,
    TInterviewProcessingJob,
} from "./TInterviewThread";
import {TJobStatusResponse, TSendMessageResponse, TTranscribeVoiceMessageResponse} from "./TInterviewThreadApi";
import InterviewThreadApiError from "./InterviewThreadApiError";

type TWarningLogger = (message: string, context: {id: unknown, role: unknown}) => void;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validationError(message: string): never {
    throw new InterviewThreadApiError({
        kind: "validation",
        message,
        retryable: false,
    });
}

function normalizeHttpUrl(value: unknown): string|null {
    if (typeof value !== "string" || value.trim() === "") {
        return null;
    }

    try {
        const url = new URL(value, "https://interview.invalid");
        return url.protocol === "http:" || url.protocol === "https:" ? value : null;
    } catch {
        return null;
    }
}

function normalizeGoalAttachment(value: unknown): TInterviewGoalAttachment|null {
    if (!isRecord(value)
        || !Number.isInteger(value.id)
        || (value.id as number) < 1
        || !isInterviewGoalAttachmentType(value.type)
    ) {
        return null;
    }

    const renderUrl = normalizeHttpUrl(value.renderUrl);
    const openUrl = normalizeHttpUrl(value.openUrl);
    const url = normalizeHttpUrl(value.url);
    if (value.type === "link" && url === null) {
        return null;
    }
    if (["image", "audio", "video"].includes(value.type) && renderUrl === null) {
        return null;
    }
    if (value.type === "document" && openUrl === null) {
        return null;
    }

    return {
        id: value.id as number,
        type: value.type,
        label: typeof value.label === "string" && value.label.trim() !== "" ? value.label : null,
        filename: typeof value.filename === "string" ? value.filename : "",
        renderUrl,
        openUrl,
        url,
    };
}

function normalizeSuggestedResponses(value: unknown): string[] {
    if (!Array.isArray(value) || value.length < 2 || value.length > 5) {
        return [];
    }

    const responses: string[] = [];
    const uniqueResponses = new Set<string>();
    for (const response of value) {
        if (typeof response !== "string") {
            return [];
        }

        const normalizedResponse = response.trim();
        if (normalizedResponse === ""
            || Array.from(normalizedResponse).length > 120
            || /[\u0000-\u001F\u007F]|<[^>]*>/u.test(normalizedResponse)
            || uniqueResponses.has(normalizedResponse)
        ) {
            return [];
        }
        responses.push(normalizedResponse);
        uniqueResponses.add(normalizedResponse);
    }

    return responses;
}

export function normalizeInterviewMessage(
    value: unknown,
    warn: TWarningLogger = (message, context) => console.warn(message, context),
): TInterviewMessage|null {
    if (!isRecord(value)) {
        warn("InterviewThread ignored an invalid message.", {id: null, role: null});
        return null;
    }

    const role = typeof value.role === "string" ? value.role.toLowerCase() : value.role;
    const contentFormat = typeof value.contentFormat === "string"
        ? value.contentFormat.toLowerCase()
        : value.contentFormat;
    if (!Number.isInteger(value.id) || typeof value.content !== "string" || !isInterviewMessageRole(role)) {
        warn("InterviewThread ignored an invalid message.", {id: value.id, role: value.role});
        return null;
    }

    return {
        id: value.id as number,
        role,
        content: value.content,
        contentFormat: isInterviewMessageContentFormat(contentFormat) ? contentFormat : "plain",
        speechAvailable: typeof value.speechAvailable === "boolean"
            ? value.speechAvailable
            : role === "agent" && value.content.trim() !== "",
        suggestedResponses: normalizeSuggestedResponses(value.suggestedResponses),
        created: typeof value.created === "string" ? value.created : undefined,
        correlationId: typeof value.correlationId === "string" ? value.correlationId : null,
        goalAttachments: Array.isArray(value.goalAttachments)
            ? value.goalAttachments
                .map(normalizeGoalAttachment)
                .filter((attachment): attachment is TInterviewGoalAttachment => attachment !== null)
            : [],
    };
}

function normalizeGoal(value: unknown): TInterviewGoalStatus|null {
    if (!isRecord(value)
        || !Number.isInteger(value.id)
        || typeof value.description !== "string"
        || typeof value.isCompleted !== "boolean"
    ) {
        return null;
    }

    return {
        id: value.id as number,
        description: value.description,
        isCompleted: value.isCompleted,
        completedAt: typeof value.completedAt === "string" ? value.completedAt : null,
    };
}

export function normalizeProcessingJob(value: unknown): TInterviewProcessingJob {
    if (!isRecord(value)
        || !Number.isInteger(value.id)
        || typeof value.type !== "string"
        || typeof value.status !== "string"
    ) {
        return validationError("The processing job response is invalid.");
    }

    return {
        id: value.id as number,
        threadId: Number.isInteger(value.threadId) ? value.threadId as number : null,
        interviewId: Number.isInteger(value.interviewId) ? value.interviewId as number : null,
        triggerMessageId: Number.isInteger(value.triggerMessageId) ? value.triggerMessageId as number : null,
        type: value.type,
        status: value.status.toLowerCase(),
        createdAt: typeof value.createdAt === "string" ? value.createdAt : "",
        processedAt: typeof value.processedAt === "string" ? value.processedAt : null,
        correlationId: typeof value.correlationId === "string" ? value.correlationId : null,
    };
}

export function normalizeInterviewThread(
    value: unknown,
    warn: TWarningLogger = (message, context) => console.warn(message, context),
): TInterviewThread {
    if (!isRecord(value)
        || !Number.isInteger(value.id)
        || typeof value.status !== "string"
        || !Array.isArray(value.messages)
    ) {
        return validationError("The interview thread response is invalid.");
    }

    const status = value.status.toLowerCase();
    if (!isInterviewThreadStatus(status)) {
        return validationError("The interview thread status is invalid.");
    }

    const messages = value.messages
        .map(message => normalizeInterviewMessage(message, warn))
        .filter((message): message is TInterviewMessage => message !== null);
    const goals = Array.isArray(value.goals)
        ? value.goals.map(normalizeGoal).filter((goal): goal is TInterviewGoalStatus => goal !== null)
        : [];

    return {
        id: value.id as number,
        status,
        startedAt: typeof value.startedAt === "string" ? value.startedAt : null,
        messagesCount: Number.isInteger(value.messagesCount) ? value.messagesCount as number : messages.length,
        lastActivityAt: typeof value.lastActivityAt === "string" ? value.lastActivityAt : null,
        lastUserMessageAt: typeof value.lastUserMessageAt === "string" ? value.lastUserMessageAt : null,
        lastAgentMessageAt: typeof value.lastAgentMessageAt === "string" ? value.lastAgentMessageAt : null,
        completedAt: typeof value.completedAt === "string" ? value.completedAt : null,
        closedAt: typeof value.closedAt === "string" ? value.closedAt : null,
        messages,
        goals,
        activeProcessingJob: value.activeProcessingJob === null || value.activeProcessingJob === undefined
            ? null
            : normalizeProcessingJob(value.activeProcessingJob),
    };
}

export function normalizeSendMessageResponse(value: unknown): TSendMessageResponse {
    if (!isRecord(value) || typeof value.success !== "boolean") {
        return validationError("The send message response is invalid.");
    }

    if (!value.success) {
        return {
            success: false,
            error: typeof value.error === "string" ? value.error : "Message could not be sent.",
        };
    }

    const message = normalizeInterviewMessage(value.message, () => undefined);
    if (message === null || !Number.isInteger(value.processingJobId)) {
        return validationError("The send message response is invalid.");
    }

    return {
        success: true,
        message,
        processingJobId: value.processingJobId as number,
    };
}

export function normalizeJobStatusResponse(value: unknown): TJobStatusResponse {
    if (!isRecord(value) || value.job === null || value.job === undefined) {
        return validationError(
            isRecord(value) && typeof value.error === "string"
                ? value.error
                : "The processing job response is invalid.",
        );
    }

    return {job: normalizeProcessingJob(value.job)};
}

export function normalizeTranscriptionResponse(value: unknown): TTranscribeVoiceMessageResponse {
    if (!isRecord(value) || typeof value.success !== "boolean") {
        return validationError("The voice transcription response is invalid.");
    }

    if (!value.success) {
        return {
            success: false,
            error: typeof value.error === "string" ? value.error : "Voice transcription failed.",
        };
    }

    if (typeof value.text !== "string") {
        return validationError("The voice transcription response is invalid.");
    }

    return {success: true, text: value.text};
}
